import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, CircularProgress, LinearProgress, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography
} from '@mui/material';
import { apiError } from '../format';
import { isCancelled } from '../api';
import { rulesApi } from './rulesApi';
import { ACTION_LABELS, BORDER, DARK, FIELD_SX, SELECT_MENU_PROPS, KIND_LABELS, KIND_ORDER, MUTED, PRIMARY, diffSnapshots, fmtDbDateTime } from './rulesConstants';
import { ActionChip, EmptyState, HEAD_ROW_SX, Panel, ScopeCell, TABLE_SX } from './RuleBits';
import { Paged, RuleAuditRow, RulesCatalog } from './rulesTypes';

interface Props { catalog: RulesCatalog; refreshKey: number }

const EMPTY_PAGE: Paged = { page: 1, limit: 25, total: 0, totalPages: 1 };

/** Вкладка «История изменений»: кто, когда и что поменял в правилах и исключениях */
const RuleAuditTab: React.FC<Props> = ({ catalog, refreshKey }) => {
  const [rows, setRows] = useState<RuleAuditRow[]>([]);
  const [pg, setPg] = useState<Paged>(EMPTY_PAGE);
  const [ruleCode, setRuleCode] = useState('');
  const [docKind, setDocKind] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((signal: AbortSignal) => {
    setLoading(true);
    rulesApi.audit({ rule_code: ruleCode, doc_kind: docKind, action, page, limit }, signal)
      .then(res => { setRows(res.rows); setPg(res.pagination); setError(null); })
      .catch(err => { if (!isCancelled(err)) setError(apiError(err, 'Не удалось загрузить историю изменений')); })
      .finally(() => { if (!signal.aborted) setLoading(false); });
  }, [ruleCode, docKind, action, page, limit]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshKey]);

  const filter = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Правило" value={ruleCode} onChange={e => filter(setRuleCode)(e.target.value)} sx={{ minWidth: 240, ...FIELD_SX }} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}>
          <MenuItem value="">Все правила</MenuItem>
          {catalog.rules.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Вид документа" value={docKind} onChange={e => filter(setDocKind)(e.target.value)} sx={{ minWidth: 190, ...FIELD_SX }} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}>
          <MenuItem value="">Все виды</MenuItem>
          {KIND_ORDER.map(k => <MenuItem key={k} value={k}>{KIND_LABELS[k]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Действие" value={action} onChange={e => filter(setAction)(e.target.value)} sx={{ minWidth: 150, ...FIELD_SX }} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}>
          <MenuItem value="">Любое</MenuItem>
          {(Object.keys(ACTION_LABELS) as Array<keyof typeof ACTION_LABELS>).map(a => <MenuItem key={a} value={a}>{ACTION_LABELS[a]}</MenuItem>)}
        </TextField>
        {loading && <CircularProgress size={18} sx={{ color: PRIMARY }} />}
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 2.5 }}>{error}</Alert>}

      <Panel sx={{ position: 'relative' }}>
        {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />}
        {!loading && rows.length === 0 ? (
          <EmptyState title="Изменений пока не было" text="Здесь появятся все правки правил сети и исключений: кто, когда и что поменял." />
        ) : (
          <TableContainer>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow sx={HEAD_ROW_SX}>
                  <TableCell>Время</TableCell>
                  <TableCell>Кто</TableCell>
                  <TableCell>Действие</TableCell>
                  <TableCell>Правило</TableCell>
                  <TableCell>Вид документа</TableCell>
                  <TableCell>Область</TableCell>
                  <TableCell>Что изменилось</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => {
                  const def = catalog.rules.find(x => x.code === r.rule_code);
                  const lines = diffSnapshots(def, r.old, r.new);
                  return (
                    <TableRow key={r.id} sx={{ '& td': { py: 1.25, verticalAlign: 'top' } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: MUTED }}>{fmtDbDateTime(r.changed_at)}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: DARK }}>{r.changed_by_name || '-'}</TableCell>
                      <TableCell><ActionChip action={r.action} /></TableCell>
                      <TableCell sx={{ color: DARK, fontWeight: 600 }}>{def?.name || r.rule_code}</TableCell>
                      <TableCell sx={{ color: MUTED }}>{KIND_LABELS[r.doc_kind]}</TableCell>
                      <TableCell><ScopeCell scopeType={r.scope_type} from={r.from_name} to={r.to_name} /></TableCell>
                      <TableCell>
                        {lines.length ? lines.map((l, i) => <Typography key={i} sx={{ fontSize: 13, color: DARK }}>{l}</Typography>) : <Typography sx={{ fontSize: 13, color: MUTED }}>-</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div" count={pg.total} page={pg.page - 1} rowsPerPage={limit} rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, p) => setPage(p + 1)} onRowsPerPageChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
          labelRowsPerPage="Строк:" labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          sx={{ borderTop: `1px solid ${BORDER}`, color: MUTED, '& .MuiTablePagination-toolbar': { minHeight: 48 } }}
        />
      </Panel>
    </Box>
  );
};

export default RuleAuditTab;
